import React, { useState, useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';

// types
import type { WThemeDetailView, WThemeGroupDetailView } from '@zola/svc-web-api-ts-client';
import type { SerializedStyles } from '@emotion/react';
import type { UserContext } from '@zola-helpers/client/dist/es/@types';

// redux
import { getUserWeddingDate } from 'selectors/user/userSelectors';
import { submitOnboarding } from 'actions/OnboardActions';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import { updateWeddingWebsiteThemeV2 } from 'actions/WeddingActions';
import { useAppSelector } from 'reducers/useAppSelector';
import { useAppDispatch } from 'reducers/useAppDispatch';

// helpers + utils
import {
  deleteSessionStorage,
  getSessionStorage,
  setCookie,
} from '@zola-helpers/client/dist/es/util/storage';
import {
  trackOnboardingCompleted,
  trackOnboardingStarted,
} from '@zola-helpers/client/dist/es/tracking/onboard/onboardEvents';
import {
  getThemeGroupUniqueIdentifier,
  getThemePdpPreviewImage,
  getThemePdpSwatchColors,
  getThemeUniqueIdentifier,
  themeSupportsSinglePage,
} from 'pages/WebsiteDesignsPLPSSR/helpers';
import { getDesktopNavHeight } from 'components/common/header/helpers';
import onboardTrackingConstants from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';

// hooks
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks/useForceUpdateOnWindowResize';
import { useIsPostAuthNav3MobileOrDesktop } from 'util/isPostAuthNav3Active';

// components
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { BreadcrumbsV2 } from '@zola/zola-ui/src/components/BreadcrumbsV2';
import { ButtonV3 } from '@zola/zola-ui/src/components/ButtonV3';
import SEORelatedLinks from '@zola/zola-ui/src/components/SEORelatedLinks';
import { LayoutMultiPageIcon } from '@zola/zola-ui/src/components/SvgIcons/LayoutMultiPage';
import { LayoutSinglePageIcon } from '@zola/zola-ui/src/components/SvgIcons/LayoutSinglePage';
import APWebsitePdp from 'cards/components/common/Samples/SingleSampleAccessPoint/SingleSampleAccessPointUI/APWebsitePdp/APWebsitePdp';
import Chip from '@zola/zola-ui/src/components/Chip';
import PdpThemeSwatches from 'pages/WebsiteDesignPdp/PdpThemeSwatches';
import TagV2, { TagV2Size } from '@zola/zola-ui/src/components/Tag/TagV2';
import WebsiteDesignPdpIntro from 'pages/WebsiteDesignPdp/WebsiteDesignPdpIntro';
import WebsiteDesignPdpMeta from 'pages/WebsiteDesignPdp/WebsiteDesignPdpMeta';
import WebsitePdpValueProps from 'pages/WebsiteDesignPdp/WebsitePdpValueProps';

// styles
import {
  ActionButtons,
  BrowserBar,
  CarouselContainer,
  Circle,
  Color,
  Dek,
  MobileBreadcrumbsFooter,
  notMobile,
  onlyMobile,
  PageDetails,
  RelatedLinksFooter,
  SiteLayoutActionsContainer,
  SiteLayoutContainer,
  SiteLayoutHeader,
  SPChipContainer,
  SplitSection,
  StyledCheckmarkIcon,
  StyledTooltip,
  SwatchContainer,
  tagMargin,
  ThemesPdpContainer,
  ThemeTitle,
  WebsitePreview,
  Page,
} from './WebsiteDesignPdp.styles';
// TODO - Replace RelatedCardsCarousel with ssr compatibile version.
import relatedLinks from './relatedLinks';

const LoadingHeightContext = React.createContext({ height: '172px' });
const { BUSINESS_UNIT, COMPONENT } = onboardTrackingConstants;
export enum LAYOUTS {
  singlepage = 'SINGLE_PAGE',
  multipage = 'MULTI_PAGE',
}

const SingleSampleAccessPoint = dynamic(
  () => import('cards/components/common/Samples/SingleSampleAccessPoint'),
  { ssr: false }
);

const RelatedCardsCarousel = dynamic(
  () => import('cards/components/common/RelatedCardsCarousel/RelatedCardsCarousel'),
  { ssr: false, loading: () => <CarouselContainer /> }
);

const CardsModalRoot = dynamic(() => import('cards/components/common/CardsModalRoot'), {
  ssr: false,
});

const UnifiedNav = dynamic(() => import('components/common/header/UnifiedNav'), {
  loading: () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const value = useContext(LoadingHeightContext);
    return <div style={value} />;
  },
  ssr: false,
});

const PreauthFooterV3 = dynamic(
  () => import('@zola/zola-ui/src/components/PreAuthV3/PreauthFooterV3'),
  { ssr: false }
);

const Footer = dynamic(
  () => import('@zola/zola-ui/src/components/Footer').then(module => module.Footer),
  { ssr: false }
);

export type WebsiteDesignPdpProps = {
  className?: string;
  isDesktop: boolean;
  showTopBanner?: boolean;
  themeKey?: string;
};

const WebsiteDesignPdp: React.FC<WebsiteDesignPdpProps> = ({
  className,
  isDesktop,
  showTopBanner = false,
  themeKey,
}) => {
  const dispatch = useAppDispatch();

  const currentTheme = useAppSelector(state => state.websitePdp.currentTheme);
  const themeGroup = useAppSelector<WThemeGroupDetailView | undefined>(
    state => state.websitePdp.themeGroup
  );
  const userContext = useAppSelector(state => state.user.userContext) as UserContext;
  const isGuest = Boolean(userContext.is_guest);
  const hasWebsite = userContext.wedding_account?.website_onboarding_completed;
  const weddingDate = useAppSelector(getUserWeddingDate);

  const themeGroupIdentifier = getThemeGroupUniqueIdentifier(themeGroup);
  const onCurrentTheme = currentTheme?.key === themeGroupIdentifier;
  const defaultTheme =
    (themeKey && themeGroup?.themes.find(theme => theme.key === themeKey)) ||
    themeGroup?.themes?.[0];
  const initialTheme = onCurrentTheme
    ? // Return current theme for post-auth users with a wedding account id and selected theme
      currentTheme
    : // Return theme by theme key in url or first theme in group
      defaultTheme;

  const [localCurrentTheme, setLocalCurrentTheme] = useState<WThemeDetailView | undefined>(
    initialTheme
  );
  const [activeLayout, setActiveLayout] = useState<string>();
  const [showDisabledLayoutTooltip, setShowDisabledLayoutTooltip] = useState(false);

  // Helper functions to access appropriate key of interest between v1 and v2 website themes
  const localCurrentThemeIdentifier = getThemeUniqueIdentifier(localCurrentTheme);
  const localCurrentThemeColor = getThemePdpSwatchColors(localCurrentTheme).primary;
  const localCurrentThemePreviewImg = getThemePdpPreviewImage(localCurrentTheme, activeLayout);
  const hasSpAsset = getThemePdpPreviewImage(localCurrentTheme, 'SINGLE_PAGE');
  const localCurrentThemeHasSinglePage = themeSupportsSinglePage(localCurrentTheme) && hasSpAsset;

  const handleClickLayoutBtn = (updatedLayout: string) => {
    setActiveLayout(updatedLayout);
  };

  const changeThemeView = (theme: WThemeDetailView) => setLocalCurrentTheme(theme);

  const onPreauthCtaClick = (): void => {
    if (localCurrentTheme) {
      const { key } = localCurrentTheme;
      const preauthWeddingThemeIdentifier = getThemeUniqueIdentifier(localCurrentTheme);
      // const oneMinute = 1 / (60 * 24);
      setCookie('preauthSelectedWeddingTheme', preauthWeddingThemeIdentifier, 1800);
      setCookie('preauthSelectedWeddingThemeKey', key, 1800);
      setCookie('preauthSelectedLayout', activeLayout, 1800);

      window.location.assign('/wedding/onboard/wedding-website/form');
    }
  };

  /**
   * For users that already have a wedding account
   */
  const setWeddingTheme = () => {
    if (localCurrentTheme && 'key' in localCurrentTheme) {
      dispatch(updateWeddingWebsiteThemeV2(`${localCurrentTheme.key}`, activeLayout))
        .then(() => {
          if (!hasWebsite) {
            const onboardingFlowId =
              getSessionStorage('wedding-website/onboarding-flow-id') || undefined;
            trackOnboardingCompleted(
              BUSINESS_UNIT.WEDDINGS,
              COMPONENT.WEDDING_WEBSITE,
              {
                isExistingUser: true,
                weddingDate,
              },
              onboardingFlowId
            );
            deleteSessionStorage('wedding-website/onboarding-flow-id');
          }
          // this key is used in the DSTD customization flow to hide an info link when a user creates their WW
          localStorage.setItem('WEDDING_WEBSITE_CREATED', JSON.stringify(true));
          window.location.assign('/wedding/manage/website/pages/home');
        })
        .catch(() => {
          dispatch(toastsActions.negative({ headline: 'Error updating your theme.' }));
        });
    }
  };

  const createWeddingAccount = () => {
    let weddingAccountDetails: {
      theme_id?: string | number;
      theme_v2_key?: string | number;
      theme_v2_layout?: string;
    } = {};
    if (localCurrentTheme) {
      const isV1 = Boolean('id' in localCurrentTheme);
      if (isV1) {
        // Website Theme V1
        weddingAccountDetails = {
          theme_id: localCurrentThemeIdentifier,
        };
      } else {
        // Website Theme V2
        // http://34.192.78.81:9140/swagger-ui/index.html#/Onboarding/completeOnboarding
        weddingAccountDetails = {
          theme_v2_key: localCurrentThemeIdentifier,
        };

        weddingAccountDetails.theme_v2_layout = activeLayout;
      }
    }
    dispatch(
      submitOnboarding({
        existing_account: true,
        business_unit: COMPONENT.WEDDING_WEBSITE,
        answers: [],
        wedding_account_details: weddingAccountDetails,
      })
    )
      .then(() => {
        trackOnboardingStarted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDINGS, true);
        trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDINGS, {
          isExistingUser: true,
          weddingDate,
        });
        trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDING_WEBSITE, {
          isExistingUser: true,
          weddingDate,
        });
        window.location.assign('/wedding/manage/website/pages/home');
      })
      .catch(() => {
        dispatch(toastsActions.negative({ headline: 'Error creating your website.' }));
      });
  };

  const getSeePreviewLink = () => {
    return `/wedding-planning/website/sample/${localCurrentTheme?.key}${
      activeLayout ? `?layout=${activeLayout}` : ''
    }`;
  };

  const renderCrossSellModules = (css: SerializedStyles) => {
    const themeTitle = themeGroup?.title;
    return (
      <>
        {!isGuest ? (
          <div css={css}>
            <CarouselContainer>
              <RelatedCardsCarousel
                family={themeTitle}
                initialVariationColor={localCurrentThemeColor}
                referenceType="website"
                lightView
                fallbackDisplay={() => <WebsitePdpValueProps />}
                useReactRouter={false}
              />
            </CarouselContainer>
          </div>
        ) : (
          <WebsitePdpValueProps css={css} />
        )}
        <div css={css}>
          <SingleSampleAccessPoint familyName={themeTitle} displayComponent={<APWebsitePdp />} />
        </div>
      </>
    );
  };

  useForceUpdateOnWindowResize();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      if (params?.layout) {
        setActiveLayout(params.layout);
      } else {
        setActiveLayout(`${LAYOUTS.multipage}`);
      }
    }
  }, []);

  const isPostAuthNav3 = useIsPostAuthNav3MobileOrDesktop(userContext);
  const navHeight = !isDesktop ? 60 : getDesktopNavHeight(isGuest, isGuest);

  const breadcrumbsProps = {
    currentSelectionLabel: themeGroup?.title as string,
    linkItems: [
      {
        label: 'Website',
        to: isGuest ? '/wedding-planning/website' : '/wedding/manage/website/manage',
      },
      {
        label: 'All Designs',
        to: isGuest ? '/wedding-planning/website/designs' : '/wedding/manage/website/designs',
      },
    ],
  };

  return (
    <Page className={className} data-testid="WebsiteDesignPdp">
      <WebsiteDesignPdpMeta themeName={localCurrentTheme?.name} themeKey={initialTheme?.key} />
      <LoadingHeightContext.Provider value={{ height: `${navHeight}px` }}>
        <UnifiedNav
          activePrimaryLink={PrimaryNavIdentifier.WEBSITE}
          secondaryType={isGuest ? undefined : 'website'}
          activeSecondaryLink={isGuest ? undefined : SecondaryNavIdentifier.DESIGNS}
          disablePrimaryNavCollapse
          initialHeight={navHeight}
        />
      </LoadingHeightContext.Provider>

      {showTopBanner ? <WebsiteDesignPdpIntro onCtaClick={onPreauthCtaClick} /> : null}
      <ThemesPdpContainer isPostAuthNav3={isPostAuthNav3}>
        <BreadcrumbsV2 {...breadcrumbsProps} />
        <SplitSection>
          <WebsitePreview
            activeLayout={activeLayout}
            data-testid="WebsiteDesignPdp__WebsitePreview"
            data-themepreviewlayout={activeLayout}
          >
            <BrowserBar />
            <img src={localCurrentThemePreviewImg} alt={themeGroup?.title} />
          </WebsitePreview>
          <PageDetails>
            <ThemeTitle data-testid="WebsiteDesignPdp__Title">
              {onCurrentTheme ? (
                <Circle>
                  <StyledCheckmarkIcon height={9} />
                </Circle>
              ) : null}
              {themeGroup?.title}
            </ThemeTitle>
            {localCurrentTheme &&
            'customization_default' in localCurrentTheme &&
            localCurrentTheme.customization_default ? (
              <TagV2
                css={tagMargin}
                size={TagV2Size.LARGE}
                emoji={{ symbol: '🎨', alt: 'color palette' }}
              >
                Extra Customizable
              </TagV2>
            ) : null}
            {themeGroup?.partner_name && (
              <p>
                Designed by{' '}
                {themeGroup?.partner_url ? (
                  <a href={themeGroup?.partner_url}>{themeGroup?.partner_name}</a>
                ) : (
                  <span>{themeGroup?.partner_name}</span>
                )}
              </p>
            )}
            {!isGuest ? (
              <Dek data-testid="WebsiteDesignPdp__Dek">{themeGroup?.description}</Dek>
            ) : (
              <div css={notMobile}>
                <Dek data-testid="WebsiteDesignPdp__Dek">{themeGroup?.description}</Dek>
              </div>
            )}

            <SiteLayoutContainer>
              <SiteLayoutHeader>Site layout</SiteLayoutHeader>
              <SiteLayoutActionsContainer>
                <SPChipContainer
                  data-testid="WebsiteDesignPdp__LayoutSP"
                  btnIsDisabled={!localCurrentThemeHasSinglePage}
                  onMouseEnter={() => setShowDisabledLayoutTooltip(true)}
                  onMouseLeave={() => setShowDisabledLayoutTooltip(false)}
                  onClick={() =>
                    localCurrentThemeHasSinglePage
                      ? handleClickLayoutBtn(`${LAYOUTS.singlepage}`)
                      : () => null
                  }
                >
                  {!localCurrentThemeHasSinglePage && showDisabledLayoutTooltip && (
                    <StyledTooltip text="Single page layout is not yet available for this design." />
                  )}
                  <Chip active={activeLayout === `${LAYOUTS.singlepage}`}>
                    <LayoutSinglePageIcon />
                    Single page
                  </Chip>
                </SPChipContainer>
                <Chip
                  active={activeLayout === `${LAYOUTS.multipage}`}
                  data-testid="WebsiteDesignPdp__LayoutMP"
                  onClick={() => handleClickLayoutBtn(`${LAYOUTS.multipage}`)}
                >
                  <LayoutMultiPageIcon />
                  Multi page
                </Chip>
              </SiteLayoutActionsContainer>
            </SiteLayoutContainer>

            <Color>Color</Color>
            <SwatchContainer>
              <PdpThemeSwatches
                themeOptions={themeGroup}
                onSwatchClick={changeThemeView}
                currentTheme={localCurrentTheme}
              />
            </SwatchContainer>
            <ActionButtons>
              {currentTheme?.key === localCurrentThemeIdentifier ? (
                <>
                  <ButtonV3 component="a" href="/wedding/manage/website/pages/home">
                    Edit Content
                  </ButtonV3>
                  <ButtonV3
                    component="a"
                    href="/wedding/manage/website/customize"
                    variant="secondary"
                  >
                    Customize Design
                  </ButtonV3>
                </>
              ) : (
                <>
                  <ButtonV3
                    onClick={() => {
                      if (userContext.has_wedding_account) {
                        setWeddingTheme();
                      } else if (userContext.has_completed_onboarding) {
                        // Create wedding account silently, with theme, and redirect
                        createWeddingAccount();
                      } else {
                        // Pre-auth or basic user
                        onPreauthCtaClick();
                      }
                    }}
                  >
                    Customize this website
                  </ButtonV3>
                  <ButtonV3
                    component="a"
                    variant="secondary"
                    href={getSeePreviewLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    See a Preview
                  </ButtonV3>
                </>
              )}
            </ActionButtons>
            {renderCrossSellModules(notMobile)}
          </PageDetails>
        </SplitSection>
        {isGuest ? (
          <div css={onlyMobile}>
            <Dek data-testid="WebsiteDesignPdp__DekMobile">{themeGroup?.description}</Dek>
          </div>
        ) : null}
        {renderCrossSellModules(onlyMobile)}
      </ThemesPdpContainer>
      <MobileBreadcrumbsFooter>
        <BreadcrumbsV2 {...breadcrumbsProps} disableBackLink />
      </MobileBreadcrumbsFooter>
      <RelatedLinksFooter>
        <SEORelatedLinks title="Explore wedding websites" relatedLinks={relatedLinks} />
      </RelatedLinksFooter>
      {isGuest ? <PreauthFooterV3 isGuest={isGuest} /> : <Footer />}
      <CardsModalRoot />
    </Page>
  );
};

export default WebsiteDesignPdp;
